// Section-aware block operations. Every block edit goes through one of
// these helpers so we never lose track of which section owns the block.
// Sections are the source of truth; the legacy flat `model.blocks` is
// kept as a mirror of the main section for the existing canvas code
// paths that still read it.
//
// v2 (block-in-block nesting): every helper that walks the page now
// recurses through container slots — `block.children`, tab items'
// `blocks`, accordion items' `blocks`, and per-column `blocks`. A new
// family of id-based helpers (`updateBlockById`, `deleteBlockById`,
// `moveBlockById`, `insertBlockAfterId`) operates on any block in the
// page, regardless of how deeply it is nested. The original section /
// index-based helpers stay as thin wrappers for top-level callers.

import type {
  Block, BlockType, PageModel, Section, TabsBlock, AccordionBlock,
  ColumnsBlock,
} from './types'
import { makeBlock } from './blocks'

// ─── Recursive slot machinery ────────────────────────────────────────
// `mapBlockSlots` applies `mapper` to every child-blocks slot a block
// owns (tabs items, accordion items, columns columns, or
// `BaseBlock.children` for the rest). Returns the original block when no
// slot changed so React can skip rerenders.

type Mapper = (childBlocks: Block[]) => Block[]

/**
 * Apply `mapper` to every child-blocks slot this block owns.
 *
 * EXPORTED because it is the only safe way to recurse into a block's
 * children: which slot holds them depends on the block TYPE, and several
 * field names are overloaded across types. Most importantly `columns` is a
 * `ColumnContent[]` on a `columns` block but a plain NUMBER on `card_grid` /
 * `feature_grid` / `stats` / `pricing` / `gallery` — so any walker that
 * reaches for `block.columns` generically crashes the moment it meets a card
 * grid. Switching on `type`, as this does, is what makes that impossible.
 * Never hand-roll a second one.
 */
export function mapBlockSlots(block: Block, mapper: Mapper): Block {
  switch (block.type) {
    case 'tabs': {
      const tabs = block as TabsBlock
      let changed = false
      const items = tabs.items.map(it => {
        const cur = it.blocks ?? []
        const next = mapper(cur)
        if (next === cur) return it
        changed = true
        return { ...it, blocks: next }
      })
      return changed ? { ...tabs, items } : block
    }
    case 'accordion': {
      const acc = block as AccordionBlock
      let changed = false
      const items = acc.items.map(it => {
        const cur = it.blocks ?? []
        const next = mapper(cur)
        if (next === cur) return it
        changed = true
        return { ...it, blocks: next }
      })
      return changed ? { ...acc, items } : block
    }
    case 'columns': {
      const cols = block as ColumnsBlock
      let changed = false
      const columns = cols.columns.map(c => {
        const cur = c.blocks ?? []
        const next = mapper(cur)
        if (next === cur) return c
        changed = true
        return { ...c, blocks: next }
      })
      return changed ? { ...cols, columns } : block
    }
    default: {
      const cur = block.children ?? []
      const next = mapper(cur)
      return next === cur ? block : { ...block, children: next }
    }
  }
}

/**
 * Recursively walk every block in `blocks`, calling `visit`. Used by id
 * uniqueness / nextId computation so collisions are detected anywhere in
 * the tree — and by anything else that needs to see every block on a page.
 *
 * EXPORTED for the same reason as `mapBlockSlots`: it switches on block
 * `type` to find the child slots, which is the only correct way to do it
 * (see that function's note about the overloaded `columns` field).
 */
export function walkBlocks(blocks: Block[], visit: (b: Block) => void): void {
  for (const b of blocks) {
    visit(b)
    switch (b.type) {
      case 'tabs':
        for (const it of b.items) walkBlocks(it.blocks ?? [], visit)
        break
      case 'accordion':
        for (const it of b.items) walkBlocks(it.blocks ?? [], visit)
        break
      case 'columns':
        for (const c of b.columns) walkBlocks(c.blocks ?? [], visit)
        break
      default:
        if (b.children?.length) walkBlocks(b.children, visit)
    }
  }
}

// Walks every block in the page (across all sections + the legacy flat
// list) so id collisions can be detected even when a block lives in a
// header/footer/aside or inside a nested container slot.
function forEachBlock(model: PageModel, visit: (b: Block) => void): void {
  for (const sec of model.sections ?? []) walkBlocks(sec.blocks, visit)
  walkBlocks(model.blocks ?? [], visit)
}

// Returns the next available `${type}_<n>` id that doesn't collide with
// any existing block id in the page. Existing blocks created before the
// human-readable format was introduced (e.g. `b_xxx_1`) are ignored when
// computing the next sequence — we only consider ids that already match
// the new format. The check uses every block id (not just same-type) so
// renaming via the property panel can also reuse this when validating
// uniqueness.
export function nextBlockIdForType(model: PageModel, type: BlockType): string {
  const re = new RegExp(`^${type}_(\\d+)$`)
  let max = 0
  const taken = new Set<string>()
  forEachBlock(model, (b) => {
    taken.add(b.id)
    const m = b.id.match(re)
    if (m) max = Math.max(max, parseInt(m[1], 10))
  })
  let n = max + 1
  let candidate = `${type}_${n}`
  while (taken.has(candidate)) {
    n += 1
    candidate = `${type}_${n}`
  }
  return candidate
}

// True when no block in `model` (other than the optional `excludeId`)
// currently uses the supplied id. Used by the property panel to validate
// user-edited block ids before they're applied. Walks nested containers.
export function isBlockIdAvailable(
  model: PageModel,
  id: string,
  excludeId?: string,
): boolean {
  if (!id) return false
  let ok = true
  forEachBlock(model, (b) => {
    if (b.id === id && b.id !== excludeId) ok = false
  })
  return ok
}

// Recursively replaces a block's id, including nested children.
function renameBlockIdInTree(blocks: Block[], oldId: string, newId: string): Block[] {
  let changed = false
  const result = blocks.map(b => {
    let nb: Block = b
    if (b.id === oldId) { nb = { ...b, id: newId }; changed = true }
    const next = mapBlockSlots(nb, (children) => renameBlockIdInTree(children, oldId, newId))
    if (next !== nb) changed = true
    return next
  })
  return changed ? result : blocks
}

// Replaces a block's id everywhere it appears (sections + nested + legacy
// flat list). The id is treated as part of the block's identity, so this
// is the single mutation path the property panel uses when the user
// manually renames a block.
export function renameBlockId(
  model: PageModel,
  oldId: string,
  newId: string,
): PageModel {
  if (oldId === newId) return model
  const sections = model.sections?.map(sec => ({
    ...sec,
    blocks: renameBlockIdInTree(sec.blocks, oldId, newId),
  }))
  const blocks = renameBlockIdInTree(model.blocks ?? [], oldId, newId)
  return { ...model, sections, blocks }
}

// ─── Recursive transform helpers ─────────────────────────────────────
// Each helper applies a single intent (replace / delete / insert-after /
// move) by walking the section tree and recursing into nested slots.
// Helpers return the same array reference when nothing changed.

type Transform = (b: Block) => Block | Block[] | null

function transformBlocks(blocks: Block[], targetId: string, transform: Transform): Block[] {
  let changed = false
  const out: Block[] = []
  for (const b of blocks) {
    if (b.id === targetId) {
      const t = transform(b)
      if (Array.isArray(t)) { out.push(...t); changed = true }
      else if (t) { out.push(t); if (t !== b) changed = true }
      else { changed = true /* dropped */ }
      continue
    }
    const next = mapBlockSlots(b, (children) => transformBlocks(children, targetId, transform))
    if (next !== b) changed = true
    out.push(next)
  }
  return changed ? out : blocks
}

function moveInBlocks(blocks: Block[], targetId: string, delta: -1 | 1): Block[] {
  const idx = blocks.findIndex(b => b.id === targetId)
  if (idx >= 0) {
    const to = idx + delta
    if (to < 0 || to >= blocks.length) return blocks
    const arr = [...blocks]
    ;[arr[idx], arr[to]] = [arr[to], arr[idx]]
    return arr
  }
  let changed = false
  const out = blocks.map(b => {
    const next = mapBlockSlots(b, (children) => moveInBlocks(children, targetId, delta))
    if (next !== b) changed = true
    return next
  })
  return changed ? out : blocks
}

// Recursively find a block by id. Returns the block reference (may be
// nested inside any container slot).
export function findBlockById(model: PageModel, blockId: string): Block | null {
  let found: Block | null = null
  forEachBlock(model, (b) => { if (!found && b.id === blockId) found = b })
  return found
}

// Apply `transform` to the block matching `blockId` anywhere in the
// page (including nested slots). Returns the same model when nothing
// changed. The legacy `model.blocks` mirror is rebuilt from the updated
// main section so v1 readers stay coherent.
function transformPage(model: PageModel, blockId: string, transform: Transform): PageModel {
  const sections = model.sections?.map(sec => {
    const blocks = transformBlocks(sec.blocks, blockId, transform)
    return blocks === sec.blocks ? sec : { ...sec, blocks }
  })
  const flat = transformBlocks(model.blocks ?? [], blockId, transform)
  const sectionsChanged = sections && sections.some((s, i) => s !== model.sections?.[i])
  const flatChanged = flat !== (model.blocks ?? [])
  if (!sectionsChanged && !flatChanged) return model
  const main = sections?.find(s => (s.tag ?? 'main') === 'main')
  return {
    ...model,
    sections: sections ?? model.sections,
    pageSchemaVersion: 2,
    blocks: main ? main.blocks : flat,
  }
}

// Replace a block (matched by id) with a patched copy. `patch` is merged
// into the existing block; pass a complete `Block` to overwrite
// wholesale. Works at any nesting depth.
export function updateBlockById(
  model: PageModel,
  blockId: string,
  patch: Partial<Block> | Block,
): PageModel {
  return transformPage(model, blockId, (b) => ({ ...b, ...patch } as Block))
}

// Delete a block (any nesting depth). When the deleted block was the
// only direct child of the main section, a fresh paragraph is added so
// the canvas isn't completely empty (mirrors the v1 behaviour).
export function deleteBlockById(model: PageModel, blockId: string): PageModel {
  const next = transformPage(model, blockId, () => null)
  // Ensure the main section is never empty.
  if (next === model) return model
  const main = next.sections?.find(s => (s.tag ?? 'main') === 'main')
  if (main && main.blocks.length === 0) {
    const placeholder = makeBlock('paragraph', nextBlockIdForType(next, 'paragraph'))
    const sections = next.sections!.map(s => s.id === main.id ? { ...s, blocks: [placeholder] } : s)
    return { ...next, sections, blocks: placeholder ? [placeholder] : next.blocks }
  }
  return next
}

// Move a block one step within the slot it currently lives in (top-level
// or nested). No-op when at boundary.
export function moveBlockById(model: PageModel, blockId: string, delta: -1 | 1): PageModel {
  const sections = model.sections?.map(sec => {
    const blocks = moveInBlocks(sec.blocks, blockId, delta)
    return blocks === sec.blocks ? sec : { ...sec, blocks }
  })
  const flat = moveInBlocks(model.blocks ?? [], blockId, delta)
  const sectionsChanged = sections && sections.some((s, i) => s !== model.sections?.[i])
  const flatChanged = flat !== (model.blocks ?? [])
  if (!sectionsChanged && !flatChanged) return model
  const main = sections?.find(s => (s.tag ?? 'main') === 'main')
  return {
    ...model,
    sections: sections ?? model.sections,
    pageSchemaVersion: 2,
    blocks: main ? main.blocks : flat,
  }
}

// Insert a fresh block of `type` immediately after the block matched by
// `afterBlockId`. Works inside any nested slot.
export function insertBlockAfterId(
  model: PageModel,
  afterBlockId: string,
  type: BlockType,
): { model: PageModel; block: Block } {
  const fresh = makeBlock(type, nextBlockIdForType(model, type), { pageName: model.obj_name })
  const next = transformPage(model, afterBlockId, (b) => [b, fresh])
  return { model: next, block: fresh }
}

// ─── Section-scoped helpers ──────────────────────────────────────────

export function findMainSectionId(model: PageModel): string | undefined {
  return model.sections?.find(s => (s.tag ?? 'main') === 'main')?.id
}

export function findSection(model: PageModel, sectionId: string): Section | undefined {
  return model.sections?.find(s => s.id === sectionId)
}

// Locate a block in the section tree. For nested blocks, the returned
// `blockIndex` is the position within the *innermost* slot, and
// `sectionId` still names the owning section. Callers that need to
// distinguish "is this nested?" should also call `findBlockById` and
// check whether the path traversed any container.
//
// For the common top-level case the return shape is unchanged.
export function findBlockLocation(
  model: PageModel,
  blockId: string,
): { sectionId: string; blockIndex: number } | null {
  for (const sec of model.sections ?? []) {
    const idx = sec.blocks.findIndex(b => b.id === blockId)
    if (idx >= 0) return { sectionId: sec.id, blockIndex: idx }
  }
  // Nested: report the section but flag blockIndex as -1 so callers
  // know reorder/insert-by-index isn't valid here. Most existing call
  // sites only use `sectionId`.
  for (const sec of model.sections ?? []) {
    let found = false
    walkBlocks(sec.blocks, (b) => { if (b.id === blockId) found = true })
    if (found) return { sectionId: sec.id, blockIndex: -1 }
  }
  return null
}

function withSection(model: PageModel, sectionId: string, mutate: (s: Section) => Section): PageModel {
  if (!model.sections) return model
  const sections = model.sections.map(s => (s.id === sectionId ? mutate(s) : s))
  const main = sections.find(s => (s.tag ?? 'main') === 'main')
  return {
    ...model,
    sections,
    pageSchemaVersion: 2,
    blocks: main?.blocks ?? model.blocks,
  }
}

// Replaces a block by id within the named section. Recurses into
// nested container slots so nested blocks are reachable by id alone.
// (The `sectionId` is informational — id lookup is global within the
// page; we keep the parameter for API compatibility.)
export function updateBlockInSection(
  model: PageModel,
  _sectionId: string,
  blockId: string,
  patch: Partial<Block> | Block,
): PageModel {
  return updateBlockById(model, blockId, patch)
}

// Insert a fresh block at `index` within the section's *top-level*
// blocks list. For nested inserts, use `insertBlockAfterId` instead.
export function insertBlockInSectionAt(
  model: PageModel,
  sectionId: string,
  index: number,
  type: BlockType,
): { model: PageModel; block: Block } {
  const block = makeBlock(type, nextBlockIdForType(model, type), { pageName: model.obj_name })
  const next = withSection(model, sectionId, sec => {
    const blocks = [...sec.blocks]
    blocks.splice(Math.max(0, Math.min(index, blocks.length)), 0, block)
    return { ...sec, blocks }
  })
  return { model: next, block }
}

// Insert after a given block id, working at any nesting depth. When
// `blockId` is null, append to the section's top-level list.
export function insertBlockInSectionAfter(
  model: PageModel,
  sectionId: string,
  blockId: string | null,
  type: BlockType,
): { model: PageModel; block: Block } {
  if (!blockId) {
    const sec = findSection(model, sectionId)
    if (!sec) {
      return { model, block: makeBlock(type, nextBlockIdForType(model, type), { pageName: model.obj_name }) }
    }
    return insertBlockInSectionAt(model, sectionId, sec.blocks.length, type)
  }
  return insertBlockAfterId(model, blockId, type)
}

// Delete by id (any nesting depth). The `sectionId` is informational.
export function deleteBlockInSection(
  model: PageModel,
  _sectionId: string,
  blockId: string,
): PageModel {
  return deleteBlockById(model, blockId)
}

// Move within the slot the block currently lives in. The `sectionId` is
// informational; the actual move is by id.
export function moveBlockWithinSection(
  model: PageModel,
  _sectionId: string,
  blockId: string,
  delta: -1 | 1,
): PageModel {
  return moveBlockById(model, blockId, delta)
}

// Move a block from its current section into `targetSectionId`, inserting at
// `targetIndex`. Same-section reorders are supported (the source removal is
// applied first, then the index is adjusted so the splice lands where the
// caller intended). No-ops if the block can't be located.
//
// Only valid for blocks living at the section's top level. Nested blocks
// stay where they are; cross-slot drag-and-drop is a follow-up.
export function moveBlockToSectionAt(
  model: PageModel,
  blockId: string,
  targetSectionId: string,
  targetIndex: number,
): PageModel {
  if (!model.sections) return model
  // Top-level only — find the source section/block index.
  let srcSectionId: string | null = null
  let srcIndex = -1
  let srcBlock: Block | null = null
  for (const sec of model.sections) {
    const idx = sec.blocks.findIndex(b => b.id === blockId)
    if (idx >= 0) { srcSectionId = sec.id; srcIndex = idx; srcBlock = sec.blocks[idx]; break }
  }
  if (!srcBlock || srcSectionId == null) return model
  if (!findSection(model, targetSectionId)) return model

  const sections = model.sections.map(sec => {
    if (sec.id === srcSectionId && sec.id === targetSectionId) {
      let idx = targetIndex
      if (srcIndex < idx) idx -= 1
      const blocks = sec.blocks.filter(b => b.id !== blockId)
      blocks.splice(Math.max(0, Math.min(idx, blocks.length)), 0, srcBlock!)
      return { ...sec, blocks }
    }
    if (sec.id === srcSectionId) {
      return { ...sec, blocks: sec.blocks.filter(b => b.id !== blockId) }
    }
    if (sec.id === targetSectionId) {
      const blocks = [...sec.blocks]
      blocks.splice(Math.max(0, Math.min(targetIndex, blocks.length)), 0, srcBlock!)
      return { ...sec, blocks }
    }
    return sec
  })
  const main = sections.find(s => (s.tag ?? 'main') === 'main')
  return {
    ...model,
    sections,
    pageSchemaVersion: 2,
    blocks: main?.blocks ?? model.blocks,
  }
}

export function updateSection(
  model: PageModel,
  sectionId: string,
  patch: Partial<Section>,
): PageModel {
  return withSection(model, sectionId, sec => ({ ...sec, ...patch }))
}
